import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import { fetchCart } from '@/store/slices/cartSlice';
import { createFromCart } from '@/store/slices/ordersSlice';
import { CartItem, DeliveryOption } from '@/types';
import { ROUTES } from '@/utils/constants';
import { getPrimaryImage, getPlaceholderImage } from '@/utils/imageUtils';
import { formatCurrency } from '@/utils/formatters';
import Button from '@/components/ui/Button';
import { CreditCardIcon, TruckIcon, MapPinIcon, CheckIcon, PhoneIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'react-hot-toast';
import { deliveryOptionApi } from '@/services/deliveryOptionService';
import { cartApi } from '@/services/api';
import { systemSettingsApi, PaymentMethodsSettings } from '@/services/systemSettingsService';

interface CheckoutForm {
  shipping_address: string;
  billing_address: string;
  phone_number: string;
  shipping_method: 'cash_on_delivery' | 'advance_payment' | 'standard';
  payment_method: 'cod' | 'prepaid' | 'card';
  delivery_method: 'courier' | 'self_delivery' | 'pickup';
  advance_amount?: number;
}

const CheckoutPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { items, totalAmount, isLoading: isCartLoading } = useSelector((state: RootState) => state.cart);
  const { isLoading: isOrderLoading, error: orderError } = useSelector((state: RootState) => state.orders);

  const [formData, setFormData] = useState<CheckoutForm>({
    shipping_address: '',
    billing_address: '',
    phone_number: user?.phone || '',
    shipping_method: 'cash_on_delivery',
    payment_method: 'cod',
    delivery_method: 'courier',
    advance_amount: 0,
  });

  const [formErrors, setFormErrors] = useState<Partial<CheckoutForm>>({});
  const [currentStep, setCurrentStep] = useState(1);

  // Delivery option state
  const [deliveryOptions, setDeliveryOptions] = useState<Record<number, DeliveryOption[]>>({});
  const [selectedDeliveryOptions, setSelectedDeliveryOptions] = useState<Record<number, number>>({});
  const [isLoadingDeliveryOptions, setIsLoadingDeliveryOptions] = useState(false);

  // Payment method settings state
  const [paymentSettings, setPaymentSettings] = useState<PaymentMethodsSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);

  useEffect(() => {
    dispatch(fetchCart());
  }, [dispatch]);

  // Fetch payment method settings
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoadingSettings(true);
      try {
        const response = await systemSettingsApi.getPaymentMethodsSettings();
        setPaymentSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch payment method settings:', error);
        // Use default settings if fetch fails
        setPaymentSettings({
          payment_methods: { cod: true, prepaid: false },
          shipping_methods: { cash_on_delivery: true, standard: false, advance_payment: false },
          delivery_methods: { courier: true, self_delivery: false, pickup: false },
        });
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchSettings();
  }, []);

  // Fetch delivery options for all cart items
  useEffect(() => {
    const fetchDeliveryOptions = async () => {
      if (!items || items.length === 0) return;

      setIsLoadingDeliveryOptions(true);
      const optionsMap: Record<number, DeliveryOption[]> = {};
      const selectedMap: Record<number, number> = {};

      try {
        for (const item of items) {
          if (item.product_id) {
            try {
              const response = await deliveryOptionApi.getProductDeliveryOptions(item.product_id);
              // Backend returns effective_options, product_options, and shop_default_options
              const options = response.data?.effective_options || response.data?.product_options || [];
              optionsMap[item.id] = options;

              // Auto-select free delivery or first option
              if (options.length > 0) {
                const freeOption = options.find(opt => opt.is_free_delivery);
                selectedMap[item.id] = freeOption?.id || options[0]?.id || 0;
              }
            } catch (error) {
              console.error(`Failed to fetch delivery options for product ${item.product_id}:`, error);
              optionsMap[item.id] = [];
            }
          }
        }

        setDeliveryOptions(optionsMap);
        setSelectedDeliveryOptions(selectedMap);
      } catch (error) {
        console.error('Error fetching delivery options:', error);
      } finally {
        setIsLoadingDeliveryOptions(false);
      }
    };

    fetchDeliveryOptions();
  }, [items]);

  // Update phone number when user data is loaded
  useEffect(() => {
    if (user?.phone && !formData.phone_number) {
      setFormData(prev => ({ ...prev, phone_number: user.phone || '' }));
    }
  }, [user?.phone]);

  // Clear error when component mounts or when form changes
  useEffect(() => {
    if (orderError) {
      // Clear error after 5 seconds or when user starts editing
      const timer = setTimeout(() => {
        // Error will be cleared by Redux when new order attempt is made
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [orderError]);

  const validateForm = (): boolean => {
    const errors: Partial<CheckoutForm> = {};

    if (!formData.shipping_address.trim()) {
      errors.shipping_address = 'Shipping address is required';
    }

    if (!formData.billing_address.trim()) {
      errors.billing_address = 'Billing address is required';
    }

    if (!formData.phone_number.trim()) {
      errors.phone_number = 'Phone number is required';
    } else if (!/^[\d\s\-\+\(\)]{10,20}$/.test(formData.phone_number.trim())) {
      errors.phone_number = 'Please enter a valid phone number (10-20 digits)';
    }

    if (formData.shipping_method === 'advance_payment' && (!formData.advance_amount || formData.advance_amount <= 0)) {
      errors.advance_amount = 'Advance amount is required for advance payment';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof CheckoutForm, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleNext = () => {
    if (currentStep === 1 && validateForm()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleDeliveryOptionChange = (cartItemId: number, optionId: number) => {
    setSelectedDeliveryOptions(prev => ({
      ...prev,
      [cartItemId]: optionId,
    }));
  };

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;

    // Update cart items with selected delivery options before placing order
    try {
      for (const item of items) {
        const selectedOptionId = selectedDeliveryOptions[item.id];
        if (selectedOptionId) {
          await cartApi.updateDeliveryOption(item.id, selectedOptionId);
        }
      }

      // Refresh cart to get updated delivery charges
      await dispatch(fetchCart()).unwrap();
    } catch (error) {
      console.error('Failed to update delivery options:', error);
      // Continue anyway - the backend might not have this endpoint yet
    }

    try {
      const orderData = {
        shipping_address: formData.shipping_address,
        billing_address: formData.billing_address,
        phone_number: formData.phone_number.trim(),
        shipping_method: formData.shipping_method,
        payment_method: formData.payment_method,
        delivery_method: formData.delivery_method,
        ...(formData.advance_amount && { advance_amount: formData.advance_amount }),
      };

      const order = await dispatch(createFromCart(orderData)).unwrap();
      
      // Ensure order has an id before navigating
      if (order && order.id) {
        // Navigate to order confirmation
        navigate(`${ROUTES.ORDERS}/${order.id}`);
      } else {
        // Fallback: navigate to orders list if order structure is unexpected
        console.error('Order created but missing id:', order);
        navigate(ROUTES.ORDERS);
        toast.error('Order created but could not load details. Please check your orders.');
      }
    } catch (error: any) {
      // Error is already handled and displayed by the thunk via toast
      // The error message will be shown in the toast notification
      console.error('Failed to place order:', error);
      
      // If error contains inventory/stock issues, the toast will show the detailed message
      // The Redux state will also have the error for UI display
    }
  };

  // Calculate totals from cart items with selected delivery options
  const productsTotal = items.reduce((total, item) => {
    return total + (item.price_at_time || item.product?.price || 0) * item.quantity;
  }, 0);

  const deliveryTotal = items.reduce((total, item) => {
    const selectedOptionId = selectedDeliveryOptions[item.id];
    if (!selectedOptionId) return total;

    const itemOptions = deliveryOptions[item.id] || [];
    const selectedOption = itemOptions.find(opt => opt.id === selectedOptionId);

    if (selectedOption) {
      // Convert delivery_charge to number to avoid string concatenation
      const charge = selectedOption.is_free_delivery ? 0 : Number(selectedOption.delivery_charge || 0);
      return total + charge;
    }

    return total;
  }, 0);

  const finalTotal = productsTotal + deliveryTotal;

  if (isCartLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <ShoppingBagIcon className="h-24 w-24 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-6">Add some items to your cart before checkout.</p>
          <Button onClick={() => navigate(ROUTES.PRODUCTS)}>
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
          <p className="mt-2 text-gray-600">Complete your order</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-8">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-primary-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 1 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                {currentStep > 1 ? <CheckIcon className="h-5 w-5" /> : '1'}
              </div>
              <span className="ml-2 font-medium">Shipping</span>
            </div>
            <div className={`w-16 h-1 ${currentStep >= 2 ? 'bg-primary-600' : 'bg-gray-200'}`}></div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-primary-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                currentStep >= 2 ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-600'
              }`}>
                2
              </div>
              <span className="ml-2 font-medium">Review</span>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {orderError && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium">
                  {typeof orderError === 'string' ? orderError : 'An error occurred while placing your order. Please try again.'}
                </p>
                {orderError && typeof orderError === 'string' && 
                 (orderError.toLowerCase().includes('inventory') || 
                  orderError.toLowerCase().includes('stock')) && (
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(ROUTES.CART)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      Update Cart to Fix Issue
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            {currentStep === 1 ? (
              /* Shipping Information */
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Shipping Information</h2>
                
                <div className="space-y-6">
                  {/* Shipping Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPinIcon className="h-4 w-4 inline mr-1" />
                      Shipping Address *
                    </label>
                    <textarea
                      value={formData.shipping_address}
                      onChange={(e) => handleInputChange('shipping_address', e.target.value)}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        formErrors.shipping_address ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your complete shipping address"
                    />
                    {formErrors.shipping_address && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.shipping_address}</p>
                    )}
                  </div>

                  {/* Billing Address */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPinIcon className="h-4 w-4 inline mr-1" />
                      Billing Address *
                    </label>
                    <textarea
                      value={formData.billing_address}
                      onChange={(e) => handleInputChange('billing_address', e.target.value)}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        formErrors.billing_address ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your complete billing address"
                    />
                    {formErrors.billing_address && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.billing_address}</p>
                    )}
                  </div>

                  {/* Phone Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <PhoneIcon className="h-4 w-4 inline mr-1" />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone_number}
                      onChange={(e) => handleInputChange('phone_number', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        formErrors.phone_number ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your phone number (e.g., +1234567890)"
                    />
                    {formErrors.phone_number && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.phone_number}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Required for delivery coordination
                    </p>
                  </div>

                  {/* Delivery Options */}
                  <div className="border-t pt-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                      <TruckIcon className="h-5 w-5 mr-2" />
                      Delivery Options for Your Items
                    </h3>

                    {isLoadingDeliveryOptions ? (
                      <div className="text-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                        <p className="mt-2 text-sm text-gray-600">Loading delivery options...</p>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {items.map((item) => {
                          const itemOptions = deliveryOptions[item.id] || [];
                          const selectedOptionId = selectedDeliveryOptions[item.id];

                          if (itemOptions.length === 0) {
                            return null; // Skip if no delivery options
                          }

                          return (
                            <div key={item.id} className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex items-start space-x-3 mb-3">
                                <img
                                  src={getPrimaryImage(item.product?.images)}
                                  alt={item.product?.name || 'Product'}
                                  className="w-12 h-12 object-cover rounded"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    if (!target.src.includes('via.placeholder.com')) {
                                      target.src = getPlaceholderImage(48, 48, 'No Image');
                                    }
                                  }}
                                />
                                <div className="flex-1">
                                  <h4 className="font-medium text-sm text-gray-900">
                                    {item.product?.name || 'Product'}
                                  </h4>
                                  <p className="text-xs text-gray-600">Qty: {item.quantity}</p>
                                </div>
                              </div>

                              <div className="space-y-2">
                                {itemOptions.map((option) => (
                                  <label
                                    key={option.id}
                                    className={`flex items-center justify-between p-3 border rounded-md cursor-pointer transition-colors ${
                                      selectedOptionId === option.id
                                        ? 'border-primary-500 bg-primary-50'
                                        : 'border-gray-300 hover:border-gray-400 bg-white'
                                    }`}
                                  >
                                    <div className="flex items-center space-x-3">
                                      <input
                                        type="radio"
                                        name={`delivery-${item.id}`}
                                        checked={selectedOptionId === option.id}
                                        onChange={() => handleDeliveryOptionChange(item.id, option.id!)}
                                        className="text-primary-600 focus:ring-primary-500"
                                      />
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {option.zone_name}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="text-sm font-semibold">
                                      {option.is_free_delivery ? (
                                        <span className="text-green-600">FREE</span>
                                      ) : (
                                        <span className="text-gray-900">
                                          {formatCurrency(option.delivery_charge || 0)}
                                        </span>
                                      )}
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Shipping Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <TruckIcon className="h-4 w-4 inline mr-1" />
                      Shipping Method *
                    </label>
                    <div className="space-y-3">
                      {/* Cash on Delivery */}
                      {paymentSettings?.shipping_methods.cash_on_delivery && (
                        <label className={`flex items-start space-x-3 p-4 rounded-lg ${
                          formData.shipping_method === 'cash_on_delivery'
                            ? 'border-2 border-primary-500 bg-primary-50'
                            : 'border border-gray-300 bg-white'
                        }`}>
                          <input
                            type="radio"
                            name="shipping_method"
                            value="cash_on_delivery"
                            checked={formData.shipping_method === 'cash_on_delivery'}
                            onChange={(e) => handleInputChange('shipping_method', e.target.value)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Cash on Delivery</div>
                            <div className="text-sm text-gray-600">Pay when you receive your order</div>
                          </div>
                        </label>
                      )}

                      {/* Standard Shipping */}
                      {paymentSettings?.shipping_methods.standard ? (
                        <label className={`flex items-start space-x-3 p-4 rounded-lg ${
                          formData.shipping_method === 'standard'
                            ? 'border-2 border-primary-500 bg-primary-50'
                            : 'border border-gray-300 bg-white'
                        }`}>
                          <input
                            type="radio"
                            name="shipping_method"
                            value="standard"
                            checked={formData.shipping_method === 'standard'}
                            onChange={(e) => handleInputChange('shipping_method', e.target.value)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Standard Shipping</div>
                            <div className="text-sm text-gray-600">Standard delivery timeline</div>
                          </div>
                        </label>
                      ) : (
                        <label className="flex items-start space-x-3 p-4 border rounded-lg bg-gray-100 opacity-60 cursor-not-allowed">
                          <input type="radio" name="shipping_method" value="standard" disabled className="mt-1" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-500">Standard Shipping</div>
                            <div className="text-sm text-gray-400">Coming soon</div>
                          </div>
                        </label>
                      )}

                      {/* Advance Payment */}
                      {paymentSettings?.shipping_methods.advance_payment ? (
                        <label className={`flex items-start space-x-3 p-4 rounded-lg ${
                          formData.shipping_method === 'advance_payment'
                            ? 'border-2 border-primary-500 bg-primary-50'
                            : 'border border-gray-300 bg-white'
                        }`}>
                          <input
                            type="radio"
                            name="shipping_method"
                            value="advance_payment"
                            checked={formData.shipping_method === 'advance_payment'}
                            onChange={(e) => handleInputChange('shipping_method', e.target.value)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Advance Payment</div>
                            <div className="text-sm text-gray-600">Pay a deposit upfront</div>
                          </div>
                        </label>
                      ) : (
                        <label className="flex items-start space-x-3 p-4 border rounded-lg bg-gray-100 opacity-60 cursor-not-allowed">
                          <input type="radio" name="shipping_method" value="advance_payment" disabled className="mt-1" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-500">Advance Payment</div>
                            <div className="text-sm text-gray-400">Coming soon</div>
                          </div>
                        </label>
                      )}
                    </div>

                    {/* Advance Payment Amount */}
                    {formData.shipping_method === 'advance_payment' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Advance Amount *
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="number"
                            value={formData.advance_amount || ''}
                            onChange={(e) => handleInputChange('advance_amount', Number(e.target.value))}
                            min="1"
                            max={finalTotal}
                            className={`w-full pl-8 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                              formErrors.advance_amount ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Enter advance amount"
                          />
                        </div>
                        {formErrors.advance_amount && (
                          <p className="mt-1 text-sm text-red-600">{formErrors.advance_amount}</p>
                        )}
                        <p className="mt-1 text-sm text-gray-500">
                          Maximum: {formatCurrency(finalTotal)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Payment Method */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <CreditCardIcon className="h-4 w-4 inline mr-1" />
                      Payment Method *
                    </label>
                    <div className="space-y-3">
                      {/* Cash on Delivery */}
                      {paymentSettings?.payment_methods.cod && (
                        <label className={`flex items-start space-x-3 p-4 rounded-lg ${
                          formData.payment_method === 'cod'
                            ? 'border-2 border-primary-500 bg-primary-50'
                            : 'border border-gray-300 bg-white'
                        }`}>
                          <input
                            type="radio"
                            name="payment_method"
                            value="cod"
                            checked={formData.payment_method === 'cod'}
                            onChange={(e) => handleInputChange('payment_method', e.target.value)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Cash on Delivery (COD)</div>
                            <div className="text-sm text-gray-600">Pay when you receive your order</div>
                          </div>
                        </label>
                      )}

                      {/* Prepaid Payment */}
                      {paymentSettings?.payment_methods.prepaid ? (
                        <label className={`flex items-start space-x-3 p-4 rounded-lg ${
                          formData.payment_method === 'prepaid'
                            ? 'border-2 border-primary-500 bg-primary-50'
                            : 'border border-gray-300 bg-white'
                        }`}>
                          <input
                            type="radio"
                            name="payment_method"
                            value="prepaid"
                            checked={formData.payment_method === 'prepaid'}
                            onChange={(e) => handleInputChange('payment_method', e.target.value)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Prepaid Payment</div>
                            <div className="text-sm text-gray-600">Pay online before delivery</div>
                          </div>
                        </label>
                      ) : (
                        <label className="flex items-start space-x-3 p-4 border rounded-lg bg-gray-100 opacity-60 cursor-not-allowed">
                          <input type="radio" name="payment_method" value="prepaid" disabled className="mt-1" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-500">Prepaid Payment</div>
                            <div className="text-sm text-gray-400">Coming soon</div>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Delivery Method */}
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <TruckIcon className="h-4 w-4 inline mr-1" />
                      Delivery Method *
                    </label>
                    <div className="space-y-3">
                      {/* Courier Delivery */}
                      {paymentSettings?.delivery_methods.courier && (
                        <label className={`flex items-start space-x-3 p-4 rounded-lg ${
                          formData.delivery_method === 'courier'
                            ? 'border-2 border-primary-500 bg-primary-50'
                            : 'border border-gray-300 bg-white'
                        }`}>
                          <input
                            type="radio"
                            name="delivery_method"
                            value="courier"
                            checked={formData.delivery_method === 'courier'}
                            onChange={(e) => handleInputChange('delivery_method', e.target.value)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Courier Delivery</div>
                            <div className="text-sm text-gray-600">Delivered by courier service</div>
                          </div>
                        </label>
                      )}

                      {/* Self Delivery */}
                      {paymentSettings?.delivery_methods.self_delivery ? (
                        <label className={`flex items-start space-x-3 p-4 rounded-lg ${
                          formData.delivery_method === 'self_delivery'
                            ? 'border-2 border-primary-500 bg-primary-50'
                            : 'border border-gray-300 bg-white'
                        }`}>
                          <input
                            type="radio"
                            name="delivery_method"
                            value="self_delivery"
                            checked={formData.delivery_method === 'self_delivery'}
                            onChange={(e) => handleInputChange('delivery_method', e.target.value)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Self Delivery</div>
                            <div className="text-sm text-gray-600">Seller delivers to you</div>
                          </div>
                        </label>
                      ) : (
                        <label className="flex items-start space-x-3 p-4 border rounded-lg bg-gray-100 opacity-60 cursor-not-allowed">
                          <input type="radio" name="delivery_method" value="self_delivery" disabled className="mt-1" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-500">Self Delivery</div>
                            <div className="text-sm text-gray-400">Coming soon</div>
                          </div>
                        </label>
                      )}

                      {/* Pickup */}
                      {paymentSettings?.delivery_methods.pickup ? (
                        <label className={`flex items-start space-x-3 p-4 rounded-lg ${
                          formData.delivery_method === 'pickup'
                            ? 'border-2 border-primary-500 bg-primary-50'
                            : 'border border-gray-300 bg-white'
                        }`}>
                          <input
                            type="radio"
                            name="delivery_method"
                            value="pickup"
                            checked={formData.delivery_method === 'pickup'}
                            onChange={(e) => handleInputChange('delivery_method', e.target.value)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Pickup</div>
                            <div className="text-sm text-gray-600">Pick up from seller's location</div>
                          </div>
                        </label>
                      ) : (
                        <label className="flex items-start space-x-3 p-4 border rounded-lg bg-gray-100 opacity-60 cursor-not-allowed">
                          <input type="radio" name="delivery_method" value="pickup" disabled className="mt-1" />
                          <div className="flex-1">
                            <div className="font-medium text-gray-500">Pickup</div>
                            <div className="text-sm text-gray-400">Coming soon</div>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-8">
                  <Button onClick={handleNext}>
                    Continue to Review
                  </Button>
                </div>
              </div>
            ) : (
              /* Order Review */
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Review Your Order</h2>
                
                {/* Order Items */}
                <div className="space-y-4 mb-6">
                  <h3 className="font-medium text-gray-900">Items in your order:</h3>
                  {items.map((item) => {
                    const imageUrl = getPrimaryImage(item.product?.images);
                    const placeholderUrl = getPlaceholderImage(64, 64, 'No Image');
                    
                    return (
                    <div key={item.id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                      <div className="flex-shrink-0">
                        <img
                          src={imageUrl}
                          alt={item.product?.name || 'Product'}
                          className="w-16 h-16 object-cover rounded-md"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            if (!target.src.includes('via.placeholder.com')) {
                              target.src = placeholderUrl;
                            }
                          }}
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{item.product?.name || 'Product'}</h4>
                        <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                        {item.delivery_zone_name && (
                          <div className="flex items-center space-x-1 mt-1 text-xs text-gray-600">
                            <TruckIcon className="h-3.5 w-3.5" />
                            <span>{item.delivery_zone_name}:</span>
                            <span className={item.delivery_charge_at_time === 0 ? 'text-green-600 font-medium' : 'text-gray-900 font-medium'}>
                              {item.delivery_charge_at_time === 0 ? 'FREE' : formatCurrency(item.delivery_charge_at_time || 0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900">
                          {formatCurrency((item.price_at_time || item.product?.price || 0) * item.quantity)}
                        </p>
                        {item.delivery_charge_at_time !== undefined && item.delivery_charge_at_time > 0 && (
                          <p className="text-xs text-gray-600 mt-1">
                            + {formatCurrency(item.delivery_charge_at_time)} delivery
                          </p>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* Shipping Information */}
                <div className="border-t pt-6">
                  <h3 className="font-medium text-gray-900 mb-4">Shipping Information:</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Address:</strong> {formData.shipping_address}
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Method:</strong> {
                        formData.shipping_method === 'cash_on_delivery' ? 'Cash on Delivery' :
                        formData.shipping_method === 'standard' ? 'Standard Shipping' :
                        'Advance Payment'
                      }
                    </p>
                    {formData.shipping_method === 'advance_payment' && formData.advance_amount && (
                      <p className="text-sm text-gray-700">
                        <strong>Advance Amount:</strong> {formatCurrency(formData.advance_amount)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <Button variant="outline" onClick={handleBack}>
                    Back to Shipping
                  </Button>
                  <Button 
                    onClick={handlePlaceOrder}
                    loading={isOrderLoading}
                    disabled={isOrderLoading}
                  >
                    <CreditCardIcon className="h-5 w-5 mr-2" />
                    Place Order
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

              <div className="space-y-3">
                {/* Products Total */}
                <div className="flex justify-between">
                  <span className="text-gray-600">Products ({items.length})</span>
                  <span className="font-medium">{formatCurrency(productsTotal)}</span>
                </div>

                {/* Delivery Charges Breakdown */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600 flex items-center">
                      <TruckIcon className="h-4 w-4 mr-1.5" />
                      Delivery Charges
                    </span>
                    <span className="font-medium">
                      {deliveryTotal === 0 ? (
                        <span className="text-green-600">FREE</span>
                      ) : (
                        formatCurrency(deliveryTotal)
                      )}
                    </span>
                  </div>

                  {/* Show delivery breakdown if multiple items with delivery */}
                  {items.length > 1 && items.some(item => item.delivery_zone_name) && (
                    <div className="ml-6 space-y-1">
                      {items.map((item) => (
                        item.delivery_zone_name && (
                          <div key={item.id} className="flex justify-between text-xs text-gray-500">
                            <span className="truncate max-w-[150px]" title={item.product?.name}>
                              {item.product?.name?.substring(0, 20)}
                              {(item.product?.name?.length || 0) > 20 ? '...' : ''}
                            </span>
                            <span className={item.delivery_charge_at_time === 0 ? 'text-green-600' : ''}>
                              {item.delivery_charge_at_time === 0 ? 'FREE' : formatCurrency(item.delivery_charge_at_time || 0)}
                            </span>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>

                {/* Advance Payment Info */}
                {formData.shipping_method === 'advance_payment' && formData.advance_amount && (
                  <div className="border-t pt-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Advance Payment</span>
                      <span className="font-medium text-primary-600">{formatCurrency(formData.advance_amount)}</span>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(finalTotal)}
                    </span>
                  </div>
                  {formData.shipping_method === 'advance_payment' && formData.advance_amount && (
                    <div className="flex justify-between mt-2 pt-2 border-t">
                      <span className="text-sm text-gray-600">Remaining on Delivery</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(finalTotal - formData.advance_amount)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
